import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  Button,
  Container,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import SignatureCanvas from "react-signature-canvas";
import { styled } from "@mui/system";
import logo from "./LogoG.jpg";
import generatePDFCC from "../tools/generatePDFCC";
import { useLocation } from "react-router-dom";
import {
  validateSecureLink,
  markLinkAsUsed,
} from "../services/firebaseLinkService";
import LinkStatusScreen from "./LinkStatusScreen";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import UploadIcon from "@mui/icons-material/Upload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

const CARD_CONFIG = {
  Visa: {
    digits: 16,
    cvv: 3,
    displayMaxLength: 19, // 16 + 3 espacios
  },
  MasterCard: {
    digits: 16,
    cvv: 3,
    displayMaxLength: 19,
  },
  Discover: {
    digits: 16,
    cvv: 3,
    displayMaxLength: 19,
  },
  Amex: {
    digits: 15,
    cvv: 4,
    displayMaxLength: 17, // 15 + 2 espacios (4-6-5)
  },
};

const getSelectedCardType = (cardType = {}) => {
  return Object.keys(cardType).find((key) => cardType[key]) || "";
};

const getCardDigitsOnly = (value = "") => {
  return value.replace(/\D/g, "");
};

const formatCardNumberByType = (value, selectedType) => {
  const digits = getCardDigitsOnly(value);

  if (!selectedType) {
    return digits.slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  }

  if (selectedType === "Amex") {
    const trimmed = digits.slice(0, 15);
    const part1 = trimmed.slice(0, 4);
    const part2 = trimmed.slice(4, 10);
    const part3 = trimmed.slice(10, 15);
    return [part1, part2, part3].filter(Boolean).join(" ");
  }

  const trimmed = digits.slice(0, 16);
  return trimmed.replace(/(.{4})/g, "$1 ").trim();
};

const isValidCardLength = (cardNumber, selectedType) => {
  if (!selectedType || !CARD_CONFIG[selectedType]) return false;
  const digits = getCardDigitsOnly(cardNumber);
  return digits.length === CARD_CONFIG[selectedType].digits;
};

const isValidCvvLength = (cvv, selectedType) => {
  if (!selectedType || !CARD_CONFIG[selectedType]) return false;
  return cvv.length === CARD_CONFIG[selectedType].cvv;
};

const formatAmount = (value) => {
  if (!value) return "0.00";

  const clean = String(value).replace(/[^0-9.]/g, "");

  return Number(clean).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function CreditCardForm() {
  const currentDate = new Date().toISOString().split("T")[0];
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const linkId = queryParams.get("linkId");

  const [cardInfo, setCardInfo] = useState({
    cardName: "",
    cardNumber: "",
    printedName: "",
    expiryDate: "",
    cvv: "",
    cardType: {},
    billingAddress: "",
    city: "",
    state: "",
    zipCode: "",
    signature: "",
    phone: "",
    date: currentDate,
    idPhoto: null,
    idPhotoName: "",
  });

  const [linkValidation, setLinkValidation] = useState({
    isValid: false,
    isLoading: true,
    status: null,
    message: "",
    linkData: null,
  });

  const sigCanvas = useRef({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const selectedCardType = getSelectedCardType(cardInfo.cardType);
  const selectedConfig = CARD_CONFIG[selectedCardType] || null;

  useEffect(() => {
    const validateLink = async () => {
      if (!linkId) {
        setLinkValidation({
          isValid: false,
          isLoading: false,
          status: "not_found",
          message: "No valid link was provided",
          linkData: null,
        });
        return;
      }

      try {
        const validation = await validateSecureLink(linkId);

        if (validation.isValid) {
          setLinkValidation({
            isValid: true,
            isLoading: false,
            status: "valid",
            message: validation.message,
            linkData: validation.linkData,
          });
        } else {
          setLinkValidation({
            isValid: false,
            isLoading: false,
            status: validation.status,
            message: validation.message,
            linkData: null,
          });
        }
      } catch (error) {
        setLinkValidation({
          isValid: false,
          isLoading: false,
          status: "error",
          message: "Error validating the link",
          linkData: null,
        });
      }
    };

    validateLink();
  }, [linkId]);

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setCardInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    const newCardType = {
      Visa: false,
      MasterCard: false,
      Amex: false,
      Discover: false,
      [name]: checked,
    };

    const newSelectedType = checked ? name : "";
    const formattedCardNumber = formatCardNumberByType(
      cardInfo.cardNumber,
      newSelectedType
    );

    setCardInfo((prev) => ({
      ...prev,
      cardType: newCardType,
      cardNumber: formattedCardNumber,
      cvv: prev.cvv.slice(
        0,
        newSelectedType ? CARD_CONFIG[newSelectedType].cvv : 4
      ),
    }));
  };

  const handleCardNumberChange = (e) => {
    const formattedValue = formatCardNumberByType(
      e.target.value,
      selectedCardType
    );

    setCardInfo((prev) => ({
      ...prev,
      cardNumber: formattedValue,
    }));
  };

  const handleCvvChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    const maxCvvLength = selectedConfig ? selectedConfig.cvv : 4;

    setCardInfo((prev) => ({
      ...prev,
      cvv: digitsOnly.slice(0, maxCvvLength),
    }));
  };

  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, "").slice(0, 4);

    if (value.length >= 3) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    }

    setCardInfo((prev) => ({
      ...prev,
      expiryDate: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target.result;
      setCardInfo((prev) => ({
        ...prev,
        idPhoto: base64String,
        idPhotoName: file.name,
      }));
    };
    reader.readAsDataURL(file);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
          aspectRatio: 16 / 9,
        },
      });

      setCameraStream(stream);
      setCameraOpen(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert("Unable to access camera. Please upload a file instead.");
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    if (isMobile) {
      canvas.width = video.videoHeight;
      canvas.height = video.videoWidth;

      context.save();
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate(Math.PI / 2);
      context.drawImage(
        video,
        -video.videoWidth / 2,
        -video.videoHeight / 2,
        video.videoWidth,
        video.videoHeight
      );
      context.restore();
    } else {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    const photoData = canvas.toDataURL("image/jpeg", 0.8);

    setCardInfo((prev) => ({
      ...prev,
      idPhoto: photoData,
      idPhotoName: `ID_Photo_${new Date().toISOString().split("T")[0]}.jpg`,
    }));

    closeCamera();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCardType) {
      alert("Please select a card type");
      return;
    }

    if (!isValidCardLength(cardInfo.cardNumber, selectedCardType)) {
      alert(
        `${selectedCardType} card number must contain exactly ${CARD_CONFIG[selectedCardType].digits} digits`
      );
      return;
    }

    if (!isValidCvvLength(cardInfo.cvv, selectedCardType)) {
      alert(
        `${selectedCardType} CVV must contain exactly ${CARD_CONFIG[selectedCardType].cvv} digits`
      );
      return;
    }

    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert("Please provide a signature");
      return;
    }

    try {
      if (linkId) {
        await markLinkAsUsed(linkId);
      }

      const signature = sigCanvas.current.toDataURL();
      const cleanedCardInfo = {
        ...cardInfo,
        cardNumber: getCardDigitsOnly(cardInfo.cardNumber),
        agentName: linkValidation.linkData?.agentName || "",
        signature,
      };

      await generatePDFCC(
        cleanedCardInfo,
        formatAmount(linkValidation.linkData?.amount),
        linkValidation.linkData?.invoiceNumber
      );

      setOpenSnackbar(true);

      setCardInfo({
        cardName: "",
        cardNumber: "",
        printedName: "",
        expiryDate: "",
        cvv: "",
        cardType: {},
        billingAddress: "",
        city: "",
        state: "",
        zipCode: "",
        signature: "",
        phone: "",
        date: currentDate,
        idPhoto: null,
        idPhotoName: "",
      });

      clearSignature();

      setTimeout(() => {
        setLinkValidation({
          isValid: false,
          isLoading: false,
          status: "already_used",
          message: "Form submitted successfully. This link is no longer valid.",
          linkData: null,
        });
      }, 2000);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Error submitting form. Please try again.");
    }
  };

  if (linkValidation.isLoading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="h6">Validating secure link...</Typography>
        </Box>
      </Container>
    );
  }

  if (!linkValidation.isValid) {
    return (
      <LinkStatusScreen
        status={linkValidation.status}
        message={linkValidation.message}
        onRetry={
          linkValidation.status === "error"
            ? () => window.location.reload()
            : null
        }
      />
    );
  }

  const StyledButton = styled(Button)({
    fontWeight: "bold",
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#ff0000",
    },
  });

  return (
    <>
      <Container component="main" maxWidth="sm">
        <TextField
          label="DATE"
          type="date"
          value={cardInfo.date}
          InputLabelProps={{ shrink: true }}
          variant="standard"
          disabled
          fullWidth
          margin="normal"
        />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            component="h1"
            variant="h5"
            style={{
              fontFamily: "Lato, sans-serif",
              textTransform: "uppercase",
              marginBottom: "5px",
            }}
          >
            Credit/Debit Card Details
          </Typography>
          <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
        </Box>

        <Typography
          variant="h6"
          style={{
            marginTop: "20px",
            fontFamily: "Lato, sans-serif",
            fontSize: "16px",
            textAlign: "left",
            marginBottom: "5px",
            textTransform: "uppercase",
          }}
        >
          <b>Invoice Number:</b> {linkValidation.linkData?.invoiceNumber}
        </Typography>

        <Typography
          variant="h6"
          style={{
            marginTop: "10px",
            fontFamily: "Lato, sans-serif",
            fontSize: "16px",
            textAlign: "left",
            marginBottom: "20px",
            textTransform: "uppercase",
          }}
        >
          <b>Amount:</b> ${formatAmount(linkValidation.linkData?.amount)}
        </Typography>

        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: "20px", textAlign: "center" }}
        >
          This secure link expires in 24 hours and can only be used once
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="Visa"
                    color="error"
                    checked={cardInfo.cardType["Visa"] || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Visa"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="MasterCard"
                    color="error"
                    checked={cardInfo.cardType["MasterCard"] || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="MasterCard"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="Amex"
                    color="error"
                    checked={cardInfo.cardType["Amex"] || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Amex"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="Discover"
                    color="error"
                    checked={cardInfo.cardType["Discover"] || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Discover"
              />
            </Grid>
          </Grid>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="cardName"
            label="Card Holder's Name"
            name="cardName"
            autoComplete="name"
            value={cardInfo.cardName}
            onChange={handleChange}
          />

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="cardNumber"
            label={
              selectedCardType
                ? `${selectedCardType} Card Number`
                : "Card Number"
            }
            type="text"
            name="cardNumber"
            autoComplete="cc-number"
            value={cardInfo.cardNumber}
            onChange={handleCardNumberChange}
            placeholder={
              selectedCardType === "Amex"
                ? "1234 123456 12345"
                : "1234 1234 1234 1234"
            }
            inputProps={{
              maxLength: selectedConfig ? selectedConfig.displayMaxLength : 19,
              inputMode: "numeric",
            }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="expiryDate"
                label="Expiry Date"
                name="expiryDate"
                type="text"
                autoComplete="cc-exp"
                placeholder="MM/YY"
                value={cardInfo.expiryDate}
                onChange={handleExpiryChange}
                inputProps={{
                  maxLength: 5,
                  inputMode: "numeric",
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="cvv"
                label={selectedCardType === "Amex" ? "CID" : "CVV"}
                type="text"
                name="cvv"
                autoComplete="cc-csc"
                value={cardInfo.cvv}
                onChange={handleCvvChange}
                inputProps={{
                  maxLength: selectedConfig ? selectedConfig.cvv : 4,
                  inputMode: "numeric",
                }}
              />
            </Grid>
          </Grid>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="billingAddress"
            label="Billing Address"
            name="billingAddress"
            value={cardInfo.billingAddress}
            onChange={handleChange}
          />

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="city"
            label="City"
            name="city"
            value={cardInfo.city}
            onChange={handleChange}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="state"
                label="State"
                name="state"
                value={cardInfo.state}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="zipCode"
                type="text"
                label="Zip Code"
                name="zipCode"
                value={cardInfo.zipCode}
                onChange={(e) =>
                  setCardInfo((prev) => ({
                    ...prev,
                    zipCode: e.target.value.slice(0, 10),
                  }))
                }
                inputProps={{
                  maxLength: 10
                }}
              />
            </Grid>
          </Grid>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="phone"
            label="Phone"
            type="text"
            name="phone"
            value={cardInfo.phone}
            onChange={(e) =>
              setCardInfo((prev) => ({
                ...prev,
                phone: e.target.value.replace(/\D/g, "").slice(0, 15),
              }))
            }
            inputProps={{
              inputMode: "numeric",
            }}
          />

          {linkValidation.linkData?.amount &&
            parseFloat(
              String(linkValidation.linkData.amount).replace(/,/g, "")
            ) >= 1500 && (
              <>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="id-photo-upload"
                  type="file"
                  onChange={handleFileChange}
                />

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  <label htmlFor="id-photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{ flex: 1 }}
                    >
                      Upload ID Photo
                    </Button>
                  </label>

                  <Button
                    variant="outlined"
                    onClick={openCamera}
                    startIcon={<CameraAltIcon />}
                    sx={{ flex: 1 }}
                  >
                    Take Photo
                  </Button>
                </Box>
              </>
            )}

          {cardInfo.idPhoto && (
            <Box sx={{ marginBottom: "16px", textAlign: "center" }}>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ marginBottom: "8px" }}
              >
                Selected file: {cardInfo.idPhotoName}
              </Typography>
              <img
                src={cardInfo.idPhoto}
                alt="ID Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              />
            </Box>
          )}

          <Typography
            variant="h6"
            style={{
              marginTop: "20px",
              fontFamily: "Lato, sans-serif",
              fontSize: "16px",
              textAlign: "center",
              marginBottom: "5px",
            }}
          >
            <b>Authorize:</b> I authorize GLOBAL TRANSIT SOLUTIONS LLC to charge
            my credit card in the amount of: ${formatAmount(linkValidation.linkData?.amount)}
          </Typography>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="printedName"
            label="Printed Name"
            name="printedName"
            value={cardInfo.printedName}
            onChange={handleChange}
          />

          <Typography
            variant="h6"
            style={{ marginTop: "20px", fontFamily: "Lato, sans-serif" }}
          >
            Signature
          </Typography>

          <SignatureCanvas
            ref={sigCanvas}
            canvasProps={{
              width: 500,
              height: 200,
              className: "signature-canvas",
            }}
            penColor="black"
          />

          <Button
            onClick={clearSignature}
            variant="outlined"
            color="error"
            style={{ marginTop: "10px", marginRight: "10px" }}
          >
            Clear Signature
          </Button>

          <StyledButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            style={{ margin: "20px 0" }}
          >
            Submit
          </StyledButton>
        </form>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={1500}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity="success">
            Payment data sent correctly. You can close the window
          </Alert>
        </Snackbar>

        <Dialog
          open={cameraOpen}
          onClose={closeCamera}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: "90vh",
              margin: "16px",
            },
          }}
        >
          <DialogTitle>
            Take ID Photo
            <IconButton
              aria-label="close"
              onClick={closeCamera}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "grey.500",
              }}
            >
              ×
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ textAlign: "center" }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  maxHeight: "60vh",
                  borderRadius: "8px",
                  backgroundColor: "#000",
                  objectFit: "contain",
                }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ marginTop: 2 }}
              >
                Position your ID document clearly in the camera view
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
            <Button onClick={closeCamera} color="error">
              Cancel
            </Button>
            <Button
              onClick={takePhoto}
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              sx={{ backgroundColor: "#000", "&:hover": { backgroundColor: "#333" } }}
            >
              Take Photo
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}